import { getIsSaveToCloudSupported } from 'owa-attachment-policy';
import AttachmentFullViewStrategy from '../../schema/AttachmentFullViewStrategy';
import AttachmentMenuAction from '../../schema/AttachmentMenuAction';
import convertMenuOptionToOpenAction from '../../utils/convertMenuOptionToOpenAction';
import convertOpenActionToMenuOption from '../../utils/convertOpenActionToMenuOption';
import getDataProviderInfo, {
    AttachmentDataProviderInfo,
    getDefaultDataProviderInfo,
} from '../../utils/DataProviderInfo/getDataProviderInfo';
import intersectionOfArrays from '../../utils/intersectionOfArrays';
import { AttachmentViewStrategy, createAttachmentViewStrategy } from 'owa-attachment-data';
import { AttachmentClass, AttachmentConstants } from 'owa-attachment-model-store';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import { isStringNullOrWhiteSpace } from 'owa-strings';
import { AttachmentPolicyChecker, AttachmentPolicyInfo } from 'owa-attachment-policy';

function isCloudyAttachment(attachment: AttachmentType) {
    return attachment.__type === AttachmentConstants.attachmentTypes.REFERENCE_ATTACHMENT_TYPE;
}

// Exporting only for testing
export function removeUnsupportedOpenActions(
    strategy: AttachmentViewStrategy,
    supportedMenuActions: AttachmentMenuAction[]
) {
    // Map all the open actions to menu actions and remove any nulls
    const supportedOpenActions: AttachmentMenuAction[] = strategy.supportedOpenActions
        .map(convertOpenActionToMenuOption)
        .filter(action => !!action);

    // Get the list of supported menu actions and convert them to open actions
    const result = intersectionOfArrays(supportedOpenActions, supportedMenuActions);
    strategy.supportedOpenActions = result.map(convertMenuOptionToOpenAction);
}

export default function createAttachmentFullViewStrategy(
    attachmentPolicyChecker: AttachmentPolicyChecker,
    attachmentPolicyInfo: AttachmentPolicyInfo,
    attachmentClass: AttachmentClass,
    attachment: AttachmentType,
    providerType: string,
    attachmentOriginalUrl: string,
    allowDownload: boolean,
    supportedMenuActions: AttachmentMenuAction[],
    isReadOnly: boolean
): AttachmentFullViewStrategy {
    const attachmentViewStrategy = createAttachmentViewStrategy(
        attachmentPolicyInfo,
        attachmentClass,
        attachment
    );
    removeUnsupportedOpenActions(attachmentViewStrategy, supportedMenuActions);

    const isCloudy = isCloudyAttachment(attachment);

    const isSaveToCloudSupported = getIsSaveToCloudSupported(attachment, isCloudy, isReadOnly);
    const isOpeningInProviderSupported: boolean = getIsOpeningInProviderSupported(
        isCloudy,
        allowDownload
    );
    const isViewingInProviderSupported: boolean = getIsViewingInProviderSupported(
        isCloudy,
        attachmentOriginalUrl
    );

    const isConvertClassicToCloudySupported: boolean = getisConvertClassicToCloudySupported(
        attachmentPolicyChecker,
        isCloudy,
        isReadOnly
    );
    const isConvertCloudyToClassicSupported: boolean = getisConvertCloudyToClassicSupported(
        attachmentPolicyChecker,
        isCloudy,
        isReadOnly,
        allowDownload,
        providerType
    );

    const isMoveAttachmentToInlineSupported: boolean = getIsMoveAttachmentToInlineSupported(
        attachmentClass,
        isCloudy,
        isReadOnly
    );

    let strategy: AttachmentFullViewStrategy;
    switch (attachmentClass) {
        case AttachmentClass.Image:
        case AttachmentClass.NativeViewableDocument:
        case AttachmentClass.WacViewableDocument:
        case AttachmentClass.Audio:
        case AttachmentClass.PdfJs:
        case AttachmentClass.ItemAttachment:
        case AttachmentClass.ItemIdAttachment:
        case AttachmentClass.Email:
        case AttachmentClass.Text:
        case AttachmentClass.Video:
        case AttachmentClass.CalendarEvent:
            strategy = createAttachmentStrategy(
                attachmentViewStrategy,
                isSaveToCloudSupported,
                isCloudy,
                isOpeningInProviderSupported,
                isViewingInProviderSupported,
                isConvertClassicToCloudySupported,
                isConvertCloudyToClassicSupported,
                isMoveAttachmentToInlineSupported
            );
            break;
        case AttachmentClass.Blocked:
            strategy = createBlockedAttachmentStrategy(attachmentViewStrategy);
            break;
        default:
            if (isCloudy) {
                strategy = createReferenceGenericAttachmentStrategy(
                    attachmentViewStrategy,
                    isOpeningInProviderSupported,
                    isViewingInProviderSupported,
                    isConvertCloudyToClassicSupported
                );
            } else {
                strategy = createClassicGenericAttachmentStrategy(
                    attachmentViewStrategy,
                    isSaveToCloudSupported,
                    isViewingInProviderSupported,
                    isConvertClassicToCloudySupported
                );
            }
            break;
    }

    strategy.supportedMenuActions = supportedMenuActions;
    return strategy;
}

export function getIsViewingInProviderSupported(
    isCloudy: boolean,
    attachmentOriginalUrl: string
): boolean {
    if (isCloudy) {
        // We no longer support view in provider for reference attachments.
        return false;
    } else {
        const isConsumerUser = isConsumer();
        const dataProviderInfo: AttachmentDataProviderInfo = getDefaultDataProviderInfo(
            isConsumerUser
        );

        // We want to show ViewInProvider after the classic attachment is saved to OneDrive.
        // Then users can access it in the data provider.
        return (
            !isStringNullOrWhiteSpace(attachmentOriginalUrl) &&
            (isConsumerUser || (dataProviderInfo && dataProviderInfo.supportViewInProvider))
        );
    }
}

export function getIsOpeningInProviderSupported(isCloudy: boolean, allowDownload: boolean) {
    return isCloudy;
}

export function getisConvertClassicToCloudySupported(
    attachmentPolicyChecker: AttachmentPolicyChecker,
    isCloudy: boolean,
    isReadOnly: boolean
): boolean {
    return attachmentPolicyChecker.convertClassicToCloudyEnabled && !isCloudy && !isReadOnly;
}

export function getisConvertCloudyToClassicSupported(
    attachmentPolicyChecker: AttachmentPolicyChecker,
    isCloudy: boolean,
    isReadOnly: boolean,
    allowDownload: boolean,
    providerType: string
): boolean {
    // allowDownload would filter out folder and ReferenceOneNoteNotebook
    if (
        attachmentPolicyChecker.convertCloudyToClassicEnabled &&
        isCloudy &&
        !isReadOnly &&
        allowDownload
    ) {
        const dataProviderInfo: AttachmentDataProviderInfo = getDataProviderInfo(providerType);
        return (dataProviderInfo && dataProviderInfo.supportConvertCloudyToClassic) || false;
    }

    return false;
}

function getIsMoveAttachmentToInlineSupported(
    attachmentClass: AttachmentClass,
    isCloudy: boolean,
    isReadOnly: boolean
): boolean {
    // Attachment must be a non-cloudy image in Compose (not read-only).
    return attachmentClass === AttachmentClass.Image && !isCloudy && !isReadOnly;
}

function createClassicGenericAttachmentStrategy(
    attachmentViewStrategy: AttachmentViewStrategy,
    isSaveToCloudSupported: boolean,
    isViewingInProviderSupported: boolean,
    isConvertClassicToCloudySupported: boolean
): AttachmentFullViewStrategy {
    return {
        ...attachmentViewStrategy,
        isSaveToCloudSupported: isSaveToCloudSupported,
        isViewingInProviderSupported: isViewingInProviderSupported,
        isOpeningInProviderSupported: false,
        isOpeningByLinkSupported: false,
        isConvertClassicToCloudySupported: isConvertClassicToCloudySupported,
        isConvertCloudyToClassicSupported: false,
        isMoveAttachmentToInlineSupported: false,
        showActionsMenu: true,
        supportedMenuActions: [],
    };
}

function createReferenceGenericAttachmentStrategy(
    attachmentViewStrategy: AttachmentViewStrategy,
    isOpeningInProviderSupported: boolean,
    isViewingInProviderSupported: boolean,
    isConvertCloudyToClassicSupported: boolean
): AttachmentFullViewStrategy {
    return {
        ...attachmentViewStrategy,
        isSaveToCloudSupported: false,
        isOpeningByLinkSupported: true,
        isOpeningInProviderSupported: isOpeningInProviderSupported,
        isViewingInProviderSupported: isViewingInProviderSupported,
        isConvertClassicToCloudySupported: false,
        isConvertCloudyToClassicSupported: isConvertCloudyToClassicSupported,
        isMoveAttachmentToInlineSupported: false,
        showActionsMenu: true,
        supportedMenuActions: [],
    };
}

function createBlockedAttachmentStrategy(
    attachmentViewStrategy: AttachmentViewStrategy
): AttachmentFullViewStrategy {
    return {
        ...attachmentViewStrategy,
        isSaveToCloudSupported: false,
        isOpeningByLinkSupported: false,
        isOpeningInProviderSupported: false,
        isViewingInProviderSupported: false,
        isConvertClassicToCloudySupported: false,
        isConvertCloudyToClassicSupported: false,
        isMoveAttachmentToInlineSupported: false,
        showActionsMenu: false,
        supportedMenuActions: [],
    };
}

function createAttachmentStrategy(
    attachmentViewStrategy: AttachmentViewStrategy,
    isSaveToCloudSupported: boolean,
    isCloudy: boolean,
    isOpeningInProviderSupported: boolean,
    isViewingInProviderSupported: boolean,
    isConvertClassicToCloudySupported: boolean,
    isConvertCloudyToClassicSupported: boolean,
    isMoveAttachmentToInlineSupported: boolean
): AttachmentFullViewStrategy {
    return {
        ...attachmentViewStrategy,
        isSaveToCloudSupported: isSaveToCloudSupported,
        isOpeningByLinkSupported: isCloudy,
        isOpeningInProviderSupported: isOpeningInProviderSupported,
        isViewingInProviderSupported: isViewingInProviderSupported,
        isConvertClassicToCloudySupported: isConvertClassicToCloudySupported,
        isConvertCloudyToClassicSupported: isConvertCloudyToClassicSupported,
        isMoveAttachmentToInlineSupported: isMoveAttachmentToInlineSupported,
        showActionsMenu: true,
        supportedMenuActions: [],
    };
}
